import * as Sentry from '@sentry/browser';
import { simpleFlashMessage } from '../utils/flashMessage';
import {
  NetworkError,
  ResponseError,
  TimeoutError,
  UnexpectedHtmlError,
} from './errors';

export const GENERIC_MESSAGE =
  '問題が発生しました。時間を空けて再度お試しください。';
export const TIMEOUT_MESSAGE =
  'タイムアウトしました。通信環境をご確認の上、時間を空けて再度お試しください。';
export const SESSION_EXPIRED_MESSAGE =
  'ログインセッションが切れました。再度ログインしてください。';

export function handleHttpError(
  error: unknown,
  { toast = true }: { toast?: boolean } = {},
): void {
  // 呼び出し側による中断はキャンセルであり失敗ではないので、通知も Sentry 送信もしない
  if (isAbortError(error)) return;

  // ネットワーク断とセッション切れ（CSRF 失敗の HTML を含む）は利用者の状態が原因なので Sentry に送らない（config/sentry.ts の ignoreErrors と同じ意図）。
  // Api::ApiController 系の CSRF 失敗は 422 の { message } で返り、RecordInvalid などの 422 と区別できないため、ここでは除外しない
  if (!(error instanceof NetworkError || isSessionExpired(error))) {
    Sentry.captureException(error);
  }

  // トーストが例外を投げても Sentry に届くよう、送った後に出す
  if (toast) simpleFlashMessage(buildErrorMessage(error), 'danger');
}

export function buildErrorMessage(error: unknown): string {
  if (error instanceof TimeoutError) return TIMEOUT_MESSAGE;
  if (isSessionExpired(error)) return SESSION_EXPIRED_MESSAGE;
  if (!(error instanceof ResponseError)) return GENERIC_MESSAGE;
  // 408 は ALB や Nginx のタイムアウト
  if (error.status === 408) return TIMEOUT_MESSAGE;
  return bodyMessage(error.body) ?? GENERIC_MESSAGE;
}

// 2xx の HTML（ログイン画面へのリダイレクトなど）と、message のない 401（Devise の `{ error }`、authenticate_user_or_admin! の `{}`）。
// message のある 401 は権限エラーや API トークンの不一致なので、セッション切れとして扱わない
function isSessionExpired(error: unknown): boolean {
  return (
    error instanceof UnexpectedHtmlError ||
    (error instanceof ResponseError &&
      error.status === 401 &&
      bodyMessage(error.body) === undefined)
  );
}

function bodyMessage(body: unknown): string | undefined {
  if (
    typeof body === 'object' &&
    body !== null &&
    'message' in body &&
    typeof body.message === 'string'
  ) {
    return body.message;
  }
  return undefined;
}

// AbortError は DOMException とは限らず、別 realm の DOMException は instanceof Error も false になる（Jest など）ため、name で判定する
function isAbortError(error: unknown): boolean {
  return (
    typeof error === 'object' &&
    error !== null &&
    'name' in error &&
    error.name === 'AbortError'
  );
}
